import {
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
  PopoverArrow,
  PopoverHeader,
  Heading,
  BoxProps,
  PopoverFooter,
} from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import { useCurrentBrandId } from "utils/currentUserHooks";
import LabeledValueTable, {
  LabeledValueRow,
} from "components/Shared/LabeledValueTable";
import { ColorfulBooleanTag, GRAY_PROPS } from "components/Shared/BooleanTag";
import DateTime from "components/Shared/DateTime";
import { MdAutoAwesome } from "react-icons/md";
import { EditIcon } from "@chakra-ui/icons";

const fragment = gql(/* GraphQL */ `
  fragment SyncIndicator on AnalyticsSyncDetail {
    isCreatedBySync
    isUpdatedBySync
    createdAt
    lastSyncAt
  }
`);

const query = gql(/* GraphQL */ `
  query SyncIndicatorBrand($brandId: UUID!) {
    brand(id: $brandId) {
      id
      isAnalyticsSyncEnabled
    }
  }
`);

export type SyncIndicatorProps = BoxProps & {
  additionalRows?: React.ReactNode;
  analyticsSyncDetails: FragmentType<typeof fragment> | null | undefined;
  footer?: React.ReactNode;
  isAddedInfoHidden?: boolean;
};

export default function SyncIndicator({
  additionalRows,
  analyticsSyncDetails,
  footer,
  isAddedInfoHidden,
  ...rest
}: SyncIndicatorProps) {
  const currentBrandId = useCurrentBrandId();

  const { data } = useBrandQuery(query, {
    variables: { brandId: currentBrandId ?? "" },
    skip: !currentBrandId,
  });

  const syncDetails = getFragmentData(fragment, analyticsSyncDetails);
  const isAnalyticsSyncEnabled = data?.brand?.isAnalyticsSyncEnabled;

  // Don't show indicator if brand analytics sync is disabled
  if (!isAnalyticsSyncEnabled) {
    return null;
  }

  const isSynced = syncDetails?.isUpdatedBySync;

  const trigger = (
    <Box
      as="span"
      display="inline-block"
      ml={2}
      cursor="help"
      px=".3em"
      aria-disabled={!syncDetails}
      _disabled={{ cursor: "wait", opacity: 0.5 }}
      {...rest}
    >
      <Box
        as="span"
        display="inline-block"
        boxSize=".525em"
        borderRadius="full"
        bg="gray.300"
        data-is-synced={isSynced}
        verticalAlign="middle"
        sx={{ "&[data-is-synced=true]": { bg: "green.300" } }}
      />
    </Box>
  );

  if (!syncDetails) return trigger;

  return (
    <Popover trigger="hover" placement="top" isLazy>
      <PopoverTrigger>{trigger}</PopoverTrigger>

      <Portal>
        <PopoverContent width="25em">
          <PopoverArrow />
          <PopoverHeader bg="gray.50">
            <Heading size="xs" color="magenta.veryDark">
              Sync Status
            </Heading>
          </PopoverHeader>
          <PopoverBody>
            <LabeledValueTable
              labelProps={{ textAlign: "right", minWidth: "9em" }}
              valueProps={{ width: "100%" }}
            >
              <LabeledValueRow
                label="Syncing?"
                value={
                  <ColorfulBooleanTag
                    value={isSynced}
                    alignSelf="flex-start"
                    falseProps={GRAY_PROPS}
                  />
                }
                cellProps={syncDetails?.lastSyncAt ? {} : { pb: ".5em" }}
              />

              {syncDetails?.lastSyncAt && (
                <LabeledValueRow
                  label="Last Synced"
                  value={
                    syncDetails?.lastSyncAt && (
                      <DateTime
                        datetime={syncDetails?.lastSyncAt}
                        format="PPPp"
                        color="gray.600"
                        fontSize=".95em"
                      />
                    )
                  }
                  cellProps={{ pb: ".5em" }}
                />
              )}

              {!isAddedInfoHidden && (
                <>
                  <LabeledValueRow
                    label="Added"
                    value={
                      <ColorfulBooleanTag
                        alignSelf="flex-start"
                        value={syncDetails?.isCreatedBySync}
                        trueLabel="Automatically"
                        falseLabel="Manually"
                        trueIcon={MdAutoAwesome}
                        falseIcon={EditIcon}
                        falseProps={GRAY_PROPS}
                      />
                    }
                    rowProps={{ borderTopWidth: 1 }}
                    cellProps={{ pt: ".5em" }}
                  />

                  <LabeledValueRow
                    label="Added On"
                    value={
                      syncDetails?.createdAt && (
                        <DateTime
                          datetime={syncDetails?.createdAt}
                          format="PPPp"
                          color="gray.600"
                          fontSize=".95em"
                        />
                      )
                    }
                    cellProps={additionalRows ? { pb: ".5em" } : undefined}
                  />
                </>
              )}

              {additionalRows}
            </LabeledValueTable>
          </PopoverBody>

          {footer && <PopoverFooter>{footer}</PopoverFooter>}
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
